/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Inject, Injectable, signal} from '@angular/core';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';

import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WorkspaceRegistryService} from '../../components/workspace-registry.service';
import {NetworkApiService} from '../admin/network/network-api.service';

@Injectable({
  providedIn: 'root'
})
export class AppShellStateService {
  private readonly networksState = signal<Record<string, any[]>>({
    ADVERTISING: [],
    DATA: [],
    POLITICAL: []
  });
  private readonly networksLoadedState = signal(false);
  private readonly appState = signal('');
  private readonly appsState = signal<string[]>([]);
  private readonly permsState = signal<any>({});
  private readonly anyPermsState = signal<any>({});
  private readonly sectionPermsState = signal<any>({});
  private readonly currentNetworkState = signal<any>(undefined);
  private readonly gravatarSizeState = signal(22);
  private readonly unreadState = signal(0);

  get networks(): Record<string, any[]> { return this.networksState(); }
  set networks(value: Record<string, any[]>) { this.networksState.set(value); }
  get networksLoaded(): boolean { return this.networksLoadedState(); }
  set networksLoaded(value: boolean) { this.networksLoadedState.set(value); }
  get app(): string { return this.appState(); }
  set app(value: string) { this.appState.set(value); }
  get apps(): string[] { return this.appsState(); }
  set apps(value: string[]) { this.appsState.set(value); }
  get perms(): any { return this.permsState(); }
  set perms(value: any) { this.permsState.set(value); }
  get anyPerms(): any { return this.anyPermsState(); }
  set anyPerms(value: any) { this.anyPermsState.set(value); }
  get sectionPerms(): any { return this.sectionPermsState(); }
  set sectionPerms(value: any) { this.sectionPermsState.set(value); }
  get currentNetwork(): any { return this.currentNetworkState(); }
  set currentNetwork(value: any) { this.currentNetworkState.set(value); }
  get gravatarSize(): number { return this.gravatarSizeState(); }
  set gravatarSize(value: number) { this.gravatarSizeState.set(value); }
  get workspaces(): any[] { return this.WorkspaceRegistry.workspaces; }
  get unread(): number { return this.unreadState(); }
  set unread(value: number) { this.unreadState.set(value); }

  private readonly networksChangeListeners = new Set<() => void>();

  constructor(
    private readonly stateService: StateService,
    @Inject(NetworkApiService) private readonly Network: NetworkApiService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly LocalUserPermissions: LocalUserPermissions,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
    private readonly WorkspaceRegistry: WorkspaceRegistryService
  ) {
  }

  get appNetworks(): any[] {
    return this.networks[this.app] || [];
  }

  /**
   * Subscribe to network list loads/updates. Used by the shell to refresh the
   * header under zoneless CD without ApplicationRef.tick() (which NG0100s
   * in-flight widgets).
   */
  onNetworksChange(listener: () => void): () => void {
    this.networksChangeListeners.add(listener);
    return () => this.networksChangeListeners.delete(listener);
  }

  bootstrap(): void {
    this.refreshShellState();

    const localUser = this.localUserProfile.get();
    if (!localUser) {
      return;
    }

    firstValueFrom(this.Network.listOptions(resourceOptionParams({ids: localUser.networkTagIds, minimal: true, useNetworkTags: true}))).then((page: any) => {
      const networks = {
        ADVERTISING: _.filter(page.results, (n: any) => n.advertisingPlatform),
        DATA: _.filter(page.results, (n: any) => n.dataPlatform),
        POLITICAL: _.filter(page.results, (n: any) => n.politicalApp)
      };
      if (!networks.POLITICAL.length && this.LocalNetworkProfile.get('politicalApp')) {
        const current = _.find(page.results, (n: any) => this.isCurrentNetwork(n));
        if (current) {
          networks.POLITICAL = [current];
        }
      }
      this.networks = networks;
      this.networksLoaded = true;
      this.syncCurrentNetwork();
      this.notifyNetworksChange();
    }, () => {
      this.networksLoaded = true;
      this.notifyNetworksChange();
    });
  }

  private notifyNetworksChange(): void {
    this.networksChangeListeners.forEach((listener) => listener());
  }

  refreshShellState(): void {
    this.app = this.localUserProfile.getCurrentApp();
    this.apps = _.filter(this.LocalUserPermissions.getApps(), (app: string) => app !== 'SELF_SERVICE');
    this.perms = this.LocalUserPermissions.getAllNetworkPermissions();
    this.anyPerms = this.LocalUserPermissions.getAnyVisibility();
    this.sectionPerms = this.LocalUserPermissions.getSectionPerms(this.app);
    this.syncCurrentNetwork();
  }

  syncCurrentNetworkFromAuthEvent(network: any): void {
    this.currentNetwork = network;
  }

  loadExternalLineItem(): void {
    const viewType = this.LocalNetworkProfile.isCalt() && this.LocalUserPermissions.canUseCaltLineItemViewType2() ? 2 : 1;
    this.stateService.go('app.adv.line-items.new', {viewType: viewType, full: viewType === 2});
  }

  switchApp(newApp: string): void {
    this.localUserProfile.set('app', newApp);
    this.stateService.go('app.home', null, {inherit: false, reload: true});
  }

  private isCurrentNetwork(n: any): boolean {
    return n.tagId === this.LocalNetworkProfile.getNetworkTag() || n.id === this.LocalNetworkProfile.get('networkId');
  }

  private syncCurrentNetwork(): void {
    if (!this.networks) {
      return;
    }
    const appNetworks = this.networks[this.app] || [];
    this.currentNetwork = _.find(appNetworks, (n: any) => this.isCurrentNetwork(n));
    this.gravatarSize = appNetworks.length > 1 ? 13 : 22;
  }
}
